<template>
  <card-minimal title="Camera setup" class="m-t-4">
    <div class="p-a-4">
      <p><strong>How are you streaming this broadcast?</strong></p>
      <div class="d-f fd-c fd-md-r m-t-2">
        <Button @click="setDevice('streamify-app')" :class="`m-t-2 m-t-md-0 m-r-md-2 bold large ${currentDevice === 'streamify-app' ? 'gray outline' : ''}`">Streamify app</Button>
        <Button @click="setDevice('larix-broadcaster')" :class="`m-t-2 m-t-md-0 m-r-md-2 bold large ${currentDevice === 'larix-broadcaster' ? 'gray outline' : ''}`">Larix Broadcaster</Button>
        <Button @click="setDevice('obs-studio')" :class="`m-t-2 m-t-md-0 m-r-md-2 bold large ${currentDevice === 'obs-studio' ? 'gray outline' : ''}`">OBS Studio</Button>
        <Button @click="setDevice('rtmp')" :class="`m-t-2 m-t-md-0 m-r-md-2 bold large ${currentDevice === 'rtmp' ? 'gray outline' : ''}`">RTMP</Button>
      </div>
    </div>
    <template v-if="currentDevice === 'streamify-app'">
      <div class="d-f fd-c fd-md-r p-x-4 p-b-4">
        <div class="p-x-4 p-t-0 p-t-md-4 p-b-4 f-2 m-t-md-0 m-t-3 ta-c ta-md-l">
          <div class="p-l-md-4">
            <h2 class="form-content-title black">Our app makes it easy to go live! ✌️</h2>
            <p class="p-y-3 l-h l-h--large f-s-large">Download the app to your phone and login with your account credentials. After that all you have to do is open the broadcast and start streaming, we recommend you start streaming 3 minutes ahead of your start time.</p>
            <p><a href="https://www.streamify.io/docs/getting-started-part-3-of-3.html" target="_blank" class="link blue f-s-large">Read more about using the Streamify App</a></p>
          </div>
        </div>
        <div class="d-f fd-c p-x-4 f-1">
          <div class="d-f f-1 ai-jc jc-c fd-c">
            <h3 class="ta-c form-content-title m-b-1">Available on iOS</h3>
            <div class="d-f ai-c jc-c m-t-1 m-t-md-0">
              <a href="https://apps.apple.com/us/app/streamify-broadcaster-2/id1565790316?itsct=apps_box_badge&amp;itscg=30200" style="display: inline-block; max-width: 166px;"><img src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&amp;releaseDate=1634774400?h=cbbab9a5ff99e5bec119f58604b35eaa" alt="Download on the App Store" style="width: 196px" /></a>
            </div>
            <div class="d-f ai-c jc-c m-t-1">
              <div class="ta-c" style="max-width: 166px; width: 100%;">
                <p class="version">Version 3.0.0</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-if="currentDevice === 'larix-broadcaster'">
      <div class="larix-disclaimer p-x-4 p-b-4 m-b-2 ta-c ta-md-l">
        <p class="">Please note that Larix without a subscription limits you to 30 minutes of streaming plus another 30 minutes with mandatory overlay. Then the stream is stopped.</p>
        <p class="">We suggest you either; 1) Use <span class="link blue" @click="setDevice('streamify-app')" >Streamifys app</span> or 2) Subscribe to Larix, which removes the time limit for streaming. The subscription option is available inside the Larix app.</p>
      </div>
      <div class="d-f fd-c fd-md-r p-b-4 p-x-1">
        <div class="p-x-3 f-1-5">
          <p class="card-content-title">
            <strong>URL</strong>
            <span>URL to stream to</span>
          </p>
          <form-input :disabled="true" :value="`${broadcast.stream.cdn.ingestion_type}://${broadcast.stream.cdn.ingestion_address}/${broadcast.stream_id}`" :copy="true" class="mono" />
          <p class="card-content-title m-t-2">
            <strong>Bitrate</strong>
          </p>
          <form-input :disabled="true" value="4000" :copy="true" class="mono" />
          <p class="card-content-title m-t-2">
            <strong>Codec</strong>
          </p>
          <form-input :disabled="true" value="H.264 (AVC)" :copy="true" class="mono" />
        </div>
        <div class="p-x-3 f-1-5 m-t-2 m-t-md-0">
          <p class="card-content-title">
            <strong>Name</strong>
          </p>
          <form-input :disabled="true" :value="streamName" :copy="true" class="mono" />
          <p class="card-content-title m-t-2">
            <strong>Resolution</strong>
          </p>
          <form-input :disabled="true" :value="broadcast.metadata.orientation === 'landscape' ? '1920x1080' : '1080x1920'" :copy="true" class="mono" />
          <p class="card-content-title m-t-2">
            <strong>Keyframe interval</strong>
          </p>
          <form-input :disabled="true" value="2" :copy="true" class="mono" />
        </div>
        <div>
          <div class="p-x-3 f-1 d-f fd-r ai-c jc-c m-t-4 m-t-md-0">
            <template v-if="useBarcode">
              <div class="qr-code">
                <canvas ref="qr" />
                <p>Scan this QR-code to load settings in Larix</p>
              </div>
            </template>
            <template v-else>
              <Button class="as-c js-c" @click="open(larixUrl)">Load settings in Larix app</Button>
            </template>
          </div>
          <div class="ta-c">
            <a href="https://apps.apple.com/us/app/larix-broadcaster/id1042474385" style="display: inline-block; max-width: 166px;">
              <img src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&amp;releaseDate=1634774400?h=cbbab9a5ff99e5bec119f58604b35eaa" alt="Download on the App Store" style="width: 196px" />
            </a>
          </div>
          <div class="ta-c">
            <a href='https://play.google.com/store/apps/details?id=com.wmspanel.larix_broadcaster&hl=en&gl=US' style="display: inline-block; max-width: 212px;">
              <img alt='Get it on Google Play' src='https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png' style="width: 250px"/>
            </a>
          </div>
        </div>
      </div>
    </template>
    <template v-else-if="currentDevice === 'obs-studio'">
      <div class="d-f fd-c fd-md-r p-b-4 p-x-1">
        <div class="p-x-3 f-1-5">
          <h2 class="ta-c form-title">Stream</h2>
          <p class="card-content-title">
            <strong>Server</strong>
            <span>URL to stream to</span>
          </p>
          <form-input :disabled="true" :value="`${broadcast.stream.cdn.ingestion_type}://${broadcast.stream.cdn.ingestion_address}`" :copy="true" class="mono" />
          <p class="card-content-title m-t-2">
            <strong>Stream Key</strong>
          </p>
          <form-input :disabled="true" :value="broadcast.stream_id" :copy="true" class="mono" />
        </div>
        <div class="p-x-3 f-1-5 m-t-2 m-t-md-0">
          <h2 class="ta-c form-title">Output</h2>
          <p class="card-content-title">
            <strong>Video Bitrate</strong>
          </p>
          <form-input :disabled="true" value="4000" :copy="true" class="mono" />
          <p class="card-content-title m-t-2">
            <strong>Encoder</strong>
          </p>
          <form-input :disabled="true" value="H.264" :copy="true" class="mono" />
          <h2 class="ta-c m-t-2 form-title">Video</h2>
          <p class="card-content-title m-t-2">
            <strong>Output Resolution</strong>
          </p>
          <form-input :disabled="true" :value="broadcast.metadata.orientation === 'landscape' ? '1920x1080' : '1080x1920'" :copy="true" class="mono" />
        </div>
      </div>
    </template>
    <template v-else-if="currentDevice === 'rtmp'">
      <div class="d-f fd-c fd-md-r p-b-4 p-x-1">
        <div class="p-x-3 f-1-5">
          <p class="card-content-title">
            <strong>Server</strong>
            <span>URL to stream to</span>
          </p>
          <form-input :disabled="true" :value="`${broadcast.stream.cdn.ingestion_type}://${broadcast.stream.cdn.ingestion_address}`" :copy="true" class="mono" />
          <p class="card-content-title m-t-2">
            <strong>Stream Key</strong>
          </p>
          <form-input :disabled="true" :value="broadcast.stream_id" :copy="true" class="mono" />
          <p class="card-content-title m-t-2">
            <strong>Bitrate</strong>
          </p>
          <form-input :disabled="true" value="4000" :copy="true" class="mono" />
        </div>
        <div class="p-x-3 f-1-5 m-t-2 m-t-md-0">
          <p class="card-content-title">
            <strong>Resolution</strong>
          </p>
          <form-input :disabled="true" :value="broadcast.metadata.orientation === 'landscape' ? '1920x1080' : '1080x1920'" :copy="true" class="mono" />
          <p class="card-content-title m-t-2">
            <strong>Keyframe interval</strong>
          </p>
          <form-input :disabled="true" value="2" :copy="true" class="mono" />
          <p class="card-content-title m-t-2">
            <strong>Codec</strong>
          </p>
          <form-input :disabled="true" value="H.264 (AVC)" :copy="true" class="mono" />
        </div>
      </div>
    </template>
  </card-minimal>
</template>

<script>

import { isAndroid, isIOS } from '@/utils';

const QRCode = require('qrcode');

export default {
  computed: {
    /**
     * Return the current broadcast in store
     * @returns {?Object}
     */
    broadcast() {
      return this.$store.getters['Broadcast/current'];
    },
    /**
     * Return the current streaming device
     * @returns {String}
     */
    currentDevice() {
      return this.$store.getters['Broadcast/streamingDevice'] || 'larix-broadcaster';
    },
    /**
     * Return the larix url for all the settings
     * @returns {String}
     */
    larixUrl() {
      const {
        broadcast: {
          stream_id,
          stream: {
            cdn: {
              ingestion_type = '',
              ingestion_address = '',
            } = {},
          } = {},
          metadata: {
            orientation,
            title,
          },
        },
      } = this;

      return `larix://set/v1?conn[][url]=${encodeURIComponent(`${ingestion_type}://${ingestion_address}/${stream_id}`)}&conn[][name]=${encodeURIComponent(title)}&conn[][mode]=av&enc[vid][res]=${orientation === 'landscape' ? '1920x1080' : '1080x1920'}&enc[vid][orientation]=${orientation}&enc[vid][format]=avc&enc[vid][keyframe]=2&enc[vid][bitrate]=4000`;
    },
    /**
     * Return the name of the stream. Use broadcast title if ephemeral stream
     * @returns {String}
     */
    streamName() {
      const {
        broadcast: {
          live_stream_id,
          metadata: {
            title,
          },
        },
      } = this;

      const streamsLength = this.$store.getters['Streams/streams'].length;
      for (let i = 0; i < streamsLength; i += 1) {
        if (this.$store.getters['Streams/streams'][i].id === live_stream_id) {
          return this.$store.getters['Streams/streams'][i].metadata.title;
        }
      }

      return title;
    },
  },
  watch: {
    /**
     * Watch for changes regarding the larix URL
     * @returns {Void}
     */
    larixUrl() {
      this.generateQRFor('larix-broadcaster');
    },
    /**
     * Watch for changes in currentDevice
     */
    currentDevice: {
      immediate: true,
      /**
       * Handler for watcher
       * @param {String} device
       * @returns {Void}
       */
      handler(device) {
        switch (device) {
          case 'larix-broadcaster':
            this.generateQRFor(device);
            break;
          default:
            break;
        }
      },
    },
  },
  methods: {
    /**
     * Open link in another window/tab
     * @param {String} link
     */
    open(link) {
      window.open(link, '_blank');
    },
    /**
     * Genereate a QR code for device
     * @param {String} device
     */
    generateQRFor(device) {
      if (!this.useBarcode) {
        return;
      }

      switch (device) {
        case 'larix-broadcaster':
          this.$nextTick(() => {
            QRCode.toCanvas(this.$el.querySelector('canvas'), this.larixUrl);
          });
          break;
        default:
          break;
      }
    },
    /**
     * Set the preferable streaming device
     * @param {String} device
     * @returns {Void}
     */
    setDevice(device) {
      this.$store.commit('Broadcast/setStreamingDevice', device);
    },
  },
  data() {
    return {
      useBarcode: true,
    };
  },
  mounted() {
    if (isAndroid() || isIOS()) {
      this.useBarcode = false;
    }
  },
};
</script>

<style lang="scss" scoped>
.version {
  color: var(--color-cta-gray);
  font-size: 14px;
  font-weight: 500;
}

.larix-disclaimer {
  color: #848893;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}

.qr-code {
  border: 1px dashed #979797;

  canvas {
    height: auto !important;
    width: 100% !important;
  }

  p {
    color: #979797;
    font-size: 14px;
    font-style: italic;
    padding: 0 25px 10px;
    text-align: center;
  }
}

.form-title {
  font-weight: 500;
  font-size: 18px;
  margin-bottom: 5px;
}

.form-content-title {
  font-size: 20px;
  font-weight: 500;
  color: var(--color-cta-gray);

  &.black {
    color: black;
  }
}
</style>
